import { ArrowRight, Mail, Building2, Trophy, Users, Target, TrendingUp } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import LogoMark from '@/components/landing/LogoMark';
import { Link } from 'react-router-dom';

const benefits = [
  {
    icon: Target,
    title: 'Measurable Edge',
    description: 'Give your athletes a weekly vision score that tracks progress and identifies blind spots no physical assessment reveals.',
  },
  {
    icon: TrendingUp,
    title: 'Performance Data',
    description: 'Integrate visual performance metrics into your existing athlete development pipeline — reaction, tracking, depth, focus.',
  },
  {
    icon: Users,
    title: 'Team-Wide Deployment',
    description: 'Roll out across squads with coach dashboards, group analytics, and individual progression paths.',
  },
  {
    icon: Trophy,
    title: 'Competitive Advantage',
    description: 'The half-second before contact is where matches are won. Train the system your competitors ignore.',
  },
];

const partnerTypes = [
  {
    label: 'Academies',
    stat: 'Foundation → Elite',
    description: 'Embed vision training into your youth development pathway. Structured progressions that grow with your athletes from baseline to elite.',
  },
  {
    label: 'Professional Teams',
    stat: '8 Components',
    description: 'Deploy across first-team and academy squads. Real-time dashboards. Weekly scores. No guesswork — just data.',
  },
  {
    label: 'Performance Centers',
    stat: 'Daily Training',
    description: 'Add vision as a pillar alongside strength, conditioning, and recovery. A complete sensory training protocol.',
  },
];

export default function PartnershipsPage() {
  return (
    <div className="noise-overlay bg-void min-h-screen">
      <Navigation />

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-void" />
          {/* Abstract grid lines */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'linear-gradient(rgba(201,168,118,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,118,0.3) 1px, transparent 1px)',
            backgroundSize: '80px 80px'
          }} />
        </div>

        {/* Floating gold ring accent */}
        <div className="absolute right-[10%] top-1/2 -translate-y-1/2 hidden lg:block">
          <div className="w-[340px] h-[340px] rounded-full border border-signal-gold/10 flex items-center justify-center animate-[spin_60s_linear_infinite]">
            <div className="w-[240px] h-[240px] rounded-full border border-signal-gold/20 flex items-center justify-center">
              <div className="w-[140px] h-[140px] rounded-full border border-signal-gold/30 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-signal-gold/60" />
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-24">
          <div className="max-w-2xl">
            <div className="w-12 h-px bg-signal-gold mb-8 opacity-0 animate-fade-up" style={{ animationDelay: '0.05s' }} />

            <p className="font-mono text-signal-gold text-xs tracking-widest uppercase mb-8 opacity-0 animate-fade-up" style={{ animationDelay: '0.1s' }}>
              Partnerships
            </p>

            <h1
              data-testid="partnerships-hero-headline"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-bone tracking-tight leading-[1.1] opacity-0 animate-fade-up"
              style={{ animationDelay: '0.25s' }}
            >
              Equip your athletes with the sense no one else trains.
            </h1>

            <p
              data-testid="partnerships-hero-subtext"
              className="font-body text-slate text-base md:text-lg max-w-xl mt-8 leading-relaxed opacity-0 animate-fade-up"
              style={{ animationDelay: '0.45s' }}
            >
              VisIQ partners with academies, professional teams, and performance centers to integrate vision training into elite athlete development.
            </p>

            <div className="mt-12 opacity-0 animate-fade-up" style={{ animationDelay: '0.6s' }}>
              <a
                data-testid="partnerships-hero-cta"
                href="mailto:hello@visiq.es"
                className="inline-flex items-center gap-3 bg-bone text-void font-body font-medium px-8 py-4 rounded-none hover:bg-signal-gold transition-colors duration-300 group"
              >
                <Mail size={18} strokeWidth={1.5} />
                Get in touch
                <ArrowRight size={18} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits grid */}
      <section data-testid="partnerships-benefits-section" className="relative py-24 md:py-32">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-signal-gold/40 to-transparent" />

        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16 md:mb-20">
            <p className="font-mono text-signal-gold text-xs tracking-widest uppercase mb-4">
              Why partner
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-bone tracking-tight">
              Vision is infrastructure.
            </h2>
            <p className="font-body text-slate text-base md:text-lg mt-5 max-w-xl">
              Not an add-on. Not a novelty. The visual system is the operating layer beneath every decision your athletes make.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
            {benefits.map((benefit) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={benefit.title}
                  data-testid={`partnerships-benefit-${benefit.title.toLowerCase().replace(/\s/g, '-')}`}
                  className="bg-surface p-8 md:p-12 group relative overflow-hidden transition-all duration-500 hover:bg-[#1c1c1c]"
                >
                  <div className="absolute top-0 left-0 w-0 h-px bg-signal-gold transition-all duration-500 group-hover:w-12" />
                  <div className="absolute top-0 left-0 w-px h-0 bg-signal-gold transition-all duration-500 group-hover:h-12" />

                  <div className="w-14 h-14 rounded-full border border-signal-gold/30 flex items-center justify-center mb-6 group-hover:border-signal-gold/60 transition-all duration-500 group-hover:shadow-[0_0_20px_rgba(201,168,118,0.1)]">
                    <Icon size={24} strokeWidth={1.5} className="text-signal-gold" />
                  </div>

                  <h3 className="font-heading text-xl font-semibold text-bone mb-3">
                    {benefit.title}
                  </h3>
                  <p className="font-body text-slate text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partner types */}
      <section data-testid="partnerships-types-section" className="relative py-24 md:py-32">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-signal-gold/40 to-transparent" />

        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16 md:mb-20">
            <p className="font-mono text-signal-gold text-xs tracking-widest uppercase mb-4">
              Who we work with
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-bone tracking-tight">
              Built for organizations that build athletes.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {partnerTypes.map((type, i) => (
              <div
                key={type.label}
                data-testid={`partnerships-type-${type.label.toLowerCase().replace(/\s/g, '-')}`}
                className="border-t border-white/10 pt-8 pb-12 md:pr-12 last:md:pr-0 group relative"
              >
                <div className="absolute top-0 left-0 w-0 h-px bg-signal-gold transition-all duration-700 group-hover:w-full" />

                <div className="flex items-center gap-3 mb-6">
                  <Building2 size={16} strokeWidth={1.5} className="text-signal-gold" />
                  <span className="font-mono text-signal-gold text-xs tracking-widest uppercase font-medium">
                    {type.label}
                  </span>
                </div>

                <div className="mb-5">
                  <span className="font-mono text-bone text-2xl font-medium">
                    {type.stat}
                  </span>
                </div>

                <p className="font-body text-slate text-sm leading-relaxed">
                  {type.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section data-testid="partnerships-contact-section" className="relative py-32 md:py-44">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-signal-gold/40 to-transparent" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-bone tracking-tight max-w-3xl mx-auto leading-[1.15]">
            Ready to give your athletes the unfair advantage?
          </h2>

          <p className="font-body text-slate text-base md:text-lg mt-6 max-w-lg mx-auto">
            Write to us to explore how VisIQ integrates into your program.
          </p>

          <div className="mt-12 flex flex-col items-center gap-6">
            <a
              data-testid="partnerships-contact-cta"
              href="mailto:hello@visiq.es"
              className="inline-flex items-center gap-3 bg-bone text-void font-body font-medium px-10 py-4 rounded-none hover:bg-signal-gold transition-colors duration-300 group"
            >
              <Mail size={18} strokeWidth={1.5} />
              hello@visiq.es
              <ArrowRight size={18} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2.5">
            <LogoMark size={20} />
            <span className="font-heading text-lg font-semibold text-bone tracking-tight">
              Vis<span className="text-signal-gold">IQ</span>
            </span>
          </div>
          <div className="flex items-center gap-8">
            <Link
              to="/"
              className="font-body text-xs text-slate hover:text-bone transition-colors duration-300"
            >
              Home
            </Link>
            <a
              href="mailto:hello@visiq.es"
              className="font-body text-xs text-slate hover:text-bone transition-colors duration-300"
            >
              hello@visiq.es
            </a>
          </div>
          <p className="font-mono text-slate text-xs tracking-wider">
            &copy; {new Date().getFullYear()} VisIQ. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
